import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { assetsApi } from 'src/Services/API';
import {
  AssetPreviewWithCommunityAndOwnerInfoDto,
  AssetPreviewWithCommunityAndOwnerInfoDtoStatusEnum,
} from 'src/Services/API/generated';

interface ToggleAssetArchiveStatusVars {
  assetId: string;
  isArchived: boolean;
}

export const useToggleAssetArchiveStatus = (
  options?: UseMutationOptions<void, unknown, ToggleAssetArchiveStatusVars>
) => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, ToggleAssetArchiveStatusVars>(
    ['toggle-asset-archive-status'],
    async ({ assetId, isArchived }: ToggleAssetArchiveStatusVars) => {
      if (!assetId) {
        throw new Error('No asset ID provided');
      }

      if (isArchived) {
        const response = await assetsApi.assetsControllerArchiveAsset(assetId);
        return response.data;
      } else {
        const response = await assetsApi.assetsControllerUnarchiveAsset(assetId);
        return response.data;
      }
    },
    {
      ...options,
      onSuccess: (data: void, variables: ToggleAssetArchiveStatusVars, context: unknown) => {
        queryClient.setQueryData<AssetPreviewWithCommunityAndOwnerInfoDto[]>(
          ['fetch-assets'],
          oldData => {
            if (!oldData) return [];
            return oldData.map(asset =>
              asset.id === variables.assetId
                ? {
                    ...asset,
                    status: variables.isArchived
                      ? AssetPreviewWithCommunityAndOwnerInfoDtoStatusEnum.Archived
                      : AssetPreviewWithCommunityAndOwnerInfoDtoStatusEnum.Active,
                  }
                : asset
            );
          }
        );

        queryClient.invalidateQueries(['fetch-asset-details', variables.assetId]);

        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};
